import emrService from '@/apis/emr/service';
import BaseTable from '@/components/bases/BaseTable';
import BaseTag from '@/components/bases/BaseTag';
import constants from '@/constants';
import emrTypeHelper from '@/helper/emrType';
import EmrRangePicker from '@/screens/CreateEmr/EmrInput/EmrRangePicker';
import EmrAction from '@/screens/Timeline/components/EmrAction';
import { usePhrStoreInContext } from '@/stores/phr';
import utils from '@/utils';
import { IRequesterResponse } from '@/utils/requester';
import { FetchNextPageOptions, InfiniteData, InfiniteQueryObserverResult } from '@tanstack/react-query';
import { TableColumnsType } from 'antd';
import dayjs from 'dayjs';
import _ from 'lodash';
import * as React from 'react';

export interface IViewTableProps {
  emr: IEMRData;
  emrType: IEMRType;
  extend?: (
    data: any,
    pointIndex: number,
    emrType: IEMRType,
    isFetching: boolean,

    fetchNextPage?: (
      options?: FetchNextPageOptions | undefined,
    ) => Promise<InfiniteQueryObserverResult<InfiniteData<IRequesterResponse<any>, unknown>, Error>>,
  ) => any;
}

const ViewTable: React.FC<IViewTableProps> = ({ emr, emrType, extend, ...props }) => {
  const endTime = dayjs(emr?.date).add(1, 's');
  const [rangeDate, setRangeDate] = React.useState<{ startTime?: dayjs.Dayjs; endTime?: dayjs.Dayjs }>({
    // startTime: endTime.subtract(7, 'day'),
    // endTime: endTime,
  });
  const [pointIndex, setPointIndex] = React.useState<number>(-1);
  const uid = usePhrStoreInContext((s) => s.uid);
  const { data, refetch, isFetchingNextPage, fetchNextPage, isFetching } = emrService.useGetEmrInfinite({
    uid,
    type: emrType.code,
    limit: 20,
    start: rangeDate.startTime?.startOf('date')?.toDate(),
    end: rangeDate.endTime?.endOf('date')?.toDate(),
  });
  const rawData = (data?.pages || []).reduce((d: IEMRData[], e) => {
    if (e?.data) d = [...d, ...e?.data];
    return d;
  }, []);

  let dataSource = rawData?.map((d: any) => emrTypeHelper.transformData({ ...d.data, date: d.date })) || [];
  dataSource = emrType.extra.formDetail.convertData ? emrType.extra.formDetail.convertData(dataSource) : dataSource;

  const renderDefault = function (value: any, record: any, index: number, f: IFieldConfig) {
    switch (f?.inputType) {
      case 'date':
      case 'time':
        return utils.safeFormat(value, f.extra.format);
      case 'shortText':
      case 'longText':
      case 'number':
        return (
          <div className="overflow-clip line-clamp-3" title={emrTypeHelper.toFix(f, value)}>
            {emrTypeHelper.toFix(f, value)}
          </div>
        );
      case 'tag':
        return value?.map?.((d: string, i: number) => (
          <BaseTag className="!whitespace-normal" key={i.toString()}>
            {d}
          </BaseTag>
        ));
      case 'select':
        const option = emrTypeHelper.getOption(f, value);
        return <div style={{ color: option?.color }}>{option?.label}</div>;
      default:
        return _.isObject(value) ? '' : value;
    }
  };

  const columns: TableColumnsType<any> = [
    ...(emrType?.extra?.formDetail?.columnFn?.(emrType?.format?.fields) || []).map((c: any) => {
      if (!c.render) {
        const fieldConfig = emrTypeHelper.getFieldByKey(c.key, emrType) as IFieldConfig;

        if (!c.render) c.render = (v: any, r: any, i: any) => renderDefault(v, r, i, fieldConfig);
      }
      return c;
    }),
    {
      key: 'action',
      title: 'Hành động',
      render(_, emr, i) {
        return (
          <div>
            <EmrAction
              emr={rawData?.[i]}
              onOk={(modal) => {
                modal.close();
                refetch();
              }}
            />
          </div>
        );
      },
    },
  ];

  return (
    <>
      <div className="flex justify-end ">
        <EmrRangePicker
          labelProps={{ className: 'w-1/2 mb-2' }}
          rangePickerProps={{
            value: [rangeDate.startTime as dayjs.Dayjs, rangeDate.endTime as dayjs.Dayjs],
            format: constants.DATE_FORMAT,
            onChange(values) {
              setRangeDate({ startTime: values?.[0] as dayjs.Dayjs, endTime: values?.[1] as dayjs.Dayjs });
            },
            presets: [
              {
                label: 'Trong ngày',
                value: [endTime.startOf('day'), endTime],
              },
              {
                label: '7 trước',
                value: [endTime.subtract(7, 'day'), endTime],
              },
              {
                label: '30 ngày trước',
                value: [endTime.subtract(30, 'day'), endTime],
              },
              {
                label: '1 năm trước',
                value: [endTime.subtract(1, 'year'), endTime],
              },
            ],

            disabledDate(current, info) {
              if (!current) return false;

              if (info.from) {
                return current > dayjs() || current < info.from;
              }

              if (current > dayjs()) return true;

              return false;
            },
          }}
        />
      </div>

      {extend && extend(dataSource, pointIndex, emrType, isFetching, fetchNextPage)}

      <div
        className="max-h-[300px] overflow-y-auto px-2"
        onScroll={(e) => {
          const element: any = e.target;
          const { scrollTop, clientHeight, scrollHeight } = element;
          if (scrollTop + clientHeight >= scrollHeight - 50) {
            !isFetchingNextPage && fetchNextPage();
          }
        }}
      >
        <BaseTable
          loading={isFetchingNextPage}
          pagination={false}
          sticky
          dataSource={dataSource}
          columns={columns}
          onRow={(record, index) => ({
            onMouseEnter: () => setPointIndex(index as number),
            onMouseLeave: () => setPointIndex(-1),
            onDoubleClick() {
              console.log({
                index,
                row: record,
                record: rawData[index as number],
              });
            },
          })}
        />
      </div>
    </>
  );
};
export default ViewTable;
